import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { AdminLayout } from '@/components/admin/AdminLayout';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Button,
  Input,
  Skeleton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@forgenetwork/ui';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Search, Download, AlertCircle } from 'lucide-react';

export const Route = createFileRoute('/admin/affiliates')({
  component: AdminAffiliatesPage,
});

interface Affiliate {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  code: string;
  status: 'active' | 'pending' | 'suspended';
  commissionRate: number;
  totalEarnings: number;
  totalReferrals: number;
  totalConversions: number;
  createdAt: string;
}

interface AffiliatesResponse {
  affiliates: Affiliate[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface AffiliateStats {
  totalAffiliates: number;
  activeAffiliates: number;
  totalEarnings: number;
  totalReferrals: number;
  conversionRate: number;
}

function AdminAffiliatesPage(): React.JSX.Element {
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<string>('all');
  const pageSize = 20;

  const { data: affiliatesData, isLoading: affiliatesLoading, error: affiliatesError } = useQuery({
    queryKey: ['admin', 'affiliates', page, search, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        ...(search && { search }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
      });
      const response = await api.get<AffiliatesResponse>(`/affiliates/admin/list?${params}`);
      
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response format from affiliates API');
      }
      if (!Array.isArray(response.affiliates)) {
        throw new Error('Affiliates data is not an array');
      }
      
      return response;
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const { data: stats, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['admin', 'affiliates', 'stats'],
    queryFn: async () => {
      const response = await api.get<AffiliateStats>('/affiliates/admin/stats');
      
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response format from affiliate stats API');
      }
      
      return response;
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({
        ...(search && { search }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
      });
      // eslint-disable-next-line dashboard/no-raw-fetch -- Export endpoint returns blob, not JSON
      const response = await fetch(`/api/v1/affiliates/admin/export?${params}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `affiliates-export-${new Date().toISOString()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to export affiliates:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return `$${(amount / 100).toFixed(2)}`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <Badge className="bg-green-500/20 text-green-700 dark:text-green-400">
            Active
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="secondary">Pending</Badge>
        );
      case 'suspended':
        return (
          <Badge variant="destructive">Suspended</Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Affiliates</h1>
            <p className="text-muted-foreground mt-2">
              Manage affiliate program and track performance
            </p>
          </div>
          <Button onClick={handleExport} variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>

        {/* Error State */}
        {(affiliatesError || statsError) && !affiliatesLoading && !statsLoading && (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <div>
                  <p className="font-medium">Failed to load affiliate data</p>
                  <p className="text-sm text-muted-foreground">
                    {affiliatesError instanceof Error
                      ? affiliatesError.message
                      : statsError instanceof Error
                        ? statsError.message
                        : 'An unexpected error occurred'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Affiliates</CardDescription>
              <CardTitle className="text-2xl">
                {statsLoading ? '...' : statsError ? '—' : stats?.totalAffiliates ?? 0}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Active</CardDescription>
              <CardTitle className="text-2xl">
                {statsLoading ? '...' : statsError ? '—' : stats?.activeAffiliates ?? 0}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Earnings</CardDescription>
              <CardTitle className="text-2xl">
                {statsLoading ? '...' : statsError ? '—' : stats ? formatCurrency(stats.totalEarnings) : '$0.00'}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Conversion Rate</CardDescription>
              <CardTitle className="text-2xl">
                {statsLoading ? '...' : statsError ? '—' : stats?.conversionRate.toFixed(1) ?? '0'}%
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle>Search & Filter</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search by name, email, or code..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Affiliates Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Affiliates</CardTitle>
            <CardDescription>
              {affiliatesData?.total
                ? `Showing ${affiliatesData.affiliates.length} of ${affiliatesData.total} affiliates`
                : 'Loading affiliates...'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {affiliatesLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : affiliatesError ? (
              <div className="text-center py-8 text-muted-foreground">
                Unable to load affiliates. Please try again.
              </div>
            ) : affiliatesData?.affiliates.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No affiliates found
              </div>
            ) : (
              <>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Affiliate</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Commission Rate</TableHead>
                        <TableHead>Referrals</TableHead>
                        <TableHead>Conversions</TableHead>
                        <TableHead className="text-right">Total Earnings</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {affiliatesData?.affiliates.map((affiliate) => (
                        <TableRow key={affiliate.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{affiliate.userName}</div>
                              <div className="text-xs text-muted-foreground">
                                {affiliate.userEmail}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <code className="text-xs bg-muted px-2 py-1 rounded">
                              {affiliate.code}
                            </code>
                          </TableCell>
                          <TableCell>{getStatusBadge(affiliate.status)}</TableCell>
                          <TableCell>{affiliate.commissionRate}%</TableCell>
                          <TableCell>{affiliate.totalReferrals}</TableCell>
                          <TableCell>{affiliate.totalConversions}</TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(affiliate.totalEarnings)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {affiliatesData && affiliatesData.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-muted-foreground">
                      Page {page} of {affiliatesData.totalPages}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(page - 1)}
                        disabled={page === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(page + 1)}
                        disabled={page === affiliatesData.totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
